import { ok } from '@easeltv/fail';
import { LoggerInstance } from '@easeltv/logger';
import { RequestResult } from '@easeltv/remote';

import { CacheMode, Config } from '../models';
import { CacheMap, DataCache } from './DataCache';
import { SDKWithSession } from './SDKWithSession';

/* ---------------------------------- Types --------------------------------- */

/**
 * These types are smart. You’ll see in implementation, as you define the options in
 * the getCachedX method, types of the following arguments are able to be code-hinted.
 *
 * TS will also tell you if the requestFn being passed into any of the getCachedX
 * methods will return data that doesn’t match the place you are trying to store it.
 */

/**
 * GetCachedResourceOptions
 *
 * Options get the getCachedResource method.
 * Infers the types based from the CacheMap for fetching a single resource by a property.
 */
export interface GetCachedResourceOptions<
    Key extends keyof CacheMap,
    Type extends CacheMap[Key],
    Name extends keyof Type,
    Identifier extends Type[Name],
> {
    cache: { key: Key; prop: Name; value: Identifier };
    bust?: boolean;
    expiry: (item: Type, config?: Config) => string;
}

/**
 * GetFirstCachedResourceOptions
 *
 * Options get the getFirstCachedResource method.
 * Infers the types based from the CacheMap for fetching a single resource by a property.
 */
export interface GetFirstCachedResourceOptions<
    Key extends keyof CacheMap,
    Type extends CacheMap[Key],
> {
    cache: { key: Key };
    bust?: boolean;
    expiry: (item: Type, config?: Config) => string;
    mode?: (item: Type, config?: Config) => CacheMode;
}

/**
 * GetCachedResourcesOptions
 *
 * Options for the getCachedResources method.
 * Infers the types based from the CacheMap for fetching a single resource by a property.
 */
export interface GetCachedResourcesOptions<
    Key extends keyof CacheMap,
    Type extends CacheMap[Key],
    Name extends keyof Type,
    Identifier extends Type[Name],
> {
    cache: { key: Key; prop: Name; value: Identifier[] };
    bust?: boolean;
    expiry: (item: Type, config?: Config) => string;
}

/**
 * PreloadCacheableResourcesOptions
 *
 * Options get the preloadCacheableResources method.
 * Infers the types based from the CacheMap for fetching a single resource by a property.
 */
export interface PreloadCacheableResourcesOptions<
    Key extends keyof CacheMap,
    Type extends CacheMap[Key],
> {
    cache: { key: Key };
    expiry: (item: Type, config?: Config) => string;
}

/* -------------------------------- CachedSDK ------------------------------- */

/**
 * CachedSDK
 *
 * This class provides us with reusable functions to be able to write api requests
 * that will first check the cache for the resource being requested.
 */
export class CachedSDK extends SDKWithSession {
    /** An SDK-wide flag to be able to turn off caching. */
    disableCaching = false;

    /** Memory instance of the data cache */
    cache: DataCache;

    constructor(
        baseUrl: string,
        tenant: string,
        apiKey: string,
        refreshTokenStorage: {
            get: () => string | undefined;
            set: (token: string) => void;
        },
        restoredCache: string | undefined,
        logger?: LoggerInstance,
    ) {
        super(baseUrl, tenant, apiKey, refreshTokenStorage, logger);

        this.cache = new DataCache(restoredCache);
    }

    /**
     * getFirstCachedResource()
     *
     * Gets the first item from the cache of the given key, or requests the resource
     * from the server.
     *
     * @param requestFn A function that returns a ValidatedResponse of the same
     *     type of the cached item being requested
     * @param cachedRequestOptions options including the key of the cache get from
     */
    getFirstCachedResource = async <
        Key extends keyof CacheMap,
        Type extends CacheMap[Key],
    >(
        requestFn: () => Promise<RequestResult<Type>>,

        {
            cache: { key },
            bust = this.disableCaching,
            expiry,
            mode,
        }: GetFirstCachedResourceOptions<Key, Type>,
    ): Promise<RequestResult<Type>> => {
        // If there is no call to bust then attempt to fetch the data from the cache
        if (!bust) {
            // Request the data from the cache.
            const resource = this.cache.getDataFirst<Key, Type>(key);

            // If the resource exists return it.
            if (resource) {
                return ok({
                    // 304 Not Modified - It is an implicit redirection to a cached resource
                    // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/304
                    // As this does not run through the validateResponse function genuine fetch requests do
                    // (see src/lib/remote/validateResponse), this will not throw an error. This is fine as all data
                    // entering the cache has already gone through response type validation when it was first requested.
                    status: 304,
                    body: resource,
                });
            }
        }

        // If we are busting or the item isn't in cache, run the request function.
        const result = await requestFn();

        // As long as the response isn't an error then we store the value in the cache.
        if (result.ok) {
            const cachedConfig = this.cache.getDataFirst('config');

            const response = result.payload;

            if (mode)
                this.cache.setCacheMode(mode(response.body, cachedConfig));

            this.cache.setData(
                key,
                response.body,
                expiry(response.body, cachedConfig),
            );
        }

        // Return the response, wrapped in a ‘success’ Result.
        return result;
    };

    /**
     * getCachedResource()
     *
     * Gets an item using a unique property from the cache of the given key, or
     * requests the resource from the server.
     *
     * @param requestFn A function that returns a ValidatedResponse of the same
     *     type of the cached item being requested
     * @param cachedRequestOptions options including the key of the cache, which
     *     unique property to use and the value of that property
     */
    getCachedResource = async <
        Key extends keyof CacheMap,
        Type extends CacheMap[Key],
        Name extends keyof Type,
        Identifier extends Type[Name],
    >(
        requestFn: (id: Identifier) => Promise<RequestResult<Type>>,
        {
            cache: { key, prop, value },
            bust = this.disableCaching,
            expiry,
        }: GetCachedResourceOptions<Key, Type, Name, Identifier>,
    ): Promise<RequestResult<Type>> => {
        // If there is no call to bust then attempt to fetch the data from the cache

        if (!bust) {
            // Request the data from the cache.
            const cachedResponse = this.cache.getData<
                Key,
                Type,
                Name,
                Identifier
            >(key, prop, value);

            // If the resource exists return it.
            if (cachedResponse) {
                return ok({
                    // 304 Not Modified - It is an implicit redirection to a cached resource
                    status: 304,
                    body: cachedResponse,
                });
            }
        }

        // If we are busting or the item isn't in cache, run the request function.
        const result = await requestFn(value);

        // As long as the response isn't an error then we store the value in the cache.
        if (result.ok) {
            const cachedConfig = this.cache.getDataFirst('config');

            const response = result.payload;

            this.cache.removeData<Key, Type, Name, Identifier>(
                key,
                prop,
                value,
            );

            this.cache.setData(
                key,
                response.body,
                expiry(response.body, cachedConfig),
            );
        }

        // Return the response, wrapped in a ‘success’ Result.
        return result;
    };

    /**
     * getCachedResources()
     *
     * Gets an array of items using an array of a unique property from the cache
     * of the given key, or requests the resource from the server.
     *
     * @param requestFn A function that returns a ValidatedResponse of the same
     *     type of the cached item being requested.
     * @param cachedRequestOptions options including the key of the cache, which
     *     unique property to use and an array of values of that property.
     */
    getCachedResources = async <
        Key extends keyof CacheMap,
        Type extends CacheMap[Key],
        Name extends keyof Type,
        Identifier extends Type[Name],
    >(
        requestFn: (
            id: Identifier[],
        ) => Promise<RequestResult<{ data: Type[] }>>,
        {
            cache: { key, prop, value },
            bust = this.disableCaching,
            expiry,
        }: GetCachedResourcesOptions<Key, Type, Name, Identifier>,
    ): Promise<RequestResult<{ data: Type[] }>> => {
        // Fetch the config for later use
        const cachedConfig = this.cache.getDataFirst('config');

        // If we're busting or we are trying to fetch a page rather than specified IDs, we can go ahead and run the
        // request without checking the cache
        if (bust || !value || value.length === 0) {
            const result = await requestFn(value);

            // If the result is not ok we can return it
            if (!result.ok) return result;

            // Otherwise we can store what we get back in the cache
            const response = result.payload;

            this.cache.setData(key, response.body.data, (item: Type) =>
                expiry(item, cachedConfig),
            );
        }

        // If we are not busting then we should first attempt to find as many of the items as possible in the cache
        const [cachedResponse, nonCachedItems] = this.cache.getDataSet<
            Key,
            Type,
            Name,
            Identifier
        >(key, prop, value);

        // If there are no nonCachedItems then all the data can be resolved and we can return the response
        if (!nonCachedItems.length) {
            return ok({
                // 304 Not Modified - It is an implicit redirection to a cached resource
                status: 304,
                body: { data: cachedResponse },
            });
        }

        // Otherwise run the request function, only for the nonCachedItems.
        const result = await requestFn(nonCachedItems);

        // If an error is returned, bail out and return it.
        if (!result.ok) return result;

        // Otherwise, update the data on the this.cache.
        const response = result.payload;
        this.cache.setData(key, response.body.data, (item: Type) =>
            expiry(item, cachedConfig),
        );

        // Return a concatenation of the cached data and the server data.
        return ok({
            ...response,
            body: {
                ...response.body,
                data: [...response.body.data, ...cachedResponse],
            },
        });
    };

    /**
     * preloadCachedResources()
     *
     * Gets an array of items using an array of a unique property from the cache
     * of the given key, or requests the resource from the server.
     *
     * @param requestFn A function that returns a ValidatedResponse of the same
     *     type of the cached item being requested.
     * @param cachedRequestOptions options including the key of the cache, which
     *     unique property to use and an array of values of that property.
     */
    preloadCacheableResources = async <
        Key extends keyof CacheMap,
        Type extends CacheMap[Key],
    >(
        requestFn: () => Promise<RequestResult<{ data: Type[] }>>,
        { cache: { key }, expiry }: PreloadCacheableResourcesOptions<Key, Type>,
    ): Promise<RequestResult<{ data: Type[] }>> => {
        // Fetch the config for later use
        const cachedConfig = this.cache.getDataFirst('config');

        const result = await requestFn();

        // If the result is not ok we can return it
        if (!result.ok) return result;

        // Otherwise we can store what we get back in the cache
        const response = result.payload;

        this.cache.setData(key, response.body.data, (item: Type) =>
            expiry(item, cachedConfig),
        );

        return result;
    };
}
